import React, { useState, useMemo, useCallback, useRef, memo } from 'react';
import { Field } from '@input';
import classNames from 'classnames';
import * as styles from './Text.module.scss';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { uniqueId } from 'lodash';

export default memo(
  React.forwardRef(function Text(
    {
      name,
      id: _id,
      className,
      fieldClassName,
      label,
      type = 'text',
      format,
      isCurrency,
      allowNegative,
      disableArrowKeyIncrement,
      theme = 'light',
      size,
      icon: _icon,
      iconPosition = 'left',
      iconStyle = {},
      iconLeft,
      iconPadding,
      iconClassName,
      style = {},
      fieldStyle,
      width,
      maxWidth,
      validate,
      inlineLabel,
      hasError,
      errorMessage,
      showErrorMessage,
      onChange,
      onFocus,
      onBlur,
      onKeyDown,
      ...restProps
    },
    ref
  ) {
    const id = useMemo(() => _id ?? uniqueId('textinput-'), [_id]);
    const errorId = useMemo(() => uniqueId('textinput-error-'), []);
    const [isFocused, setIsFocused] = useState(false);
    const isNumber = !!isCurrency;
    const isPattern = !!(format || restProps.valueIsNumericString);
    const isFormatted = isNumber || isPattern;

    const value = restProps.value;
    const valueRef = useRef(value);

    if ('value' in restProps) {
      valueRef.current = value;
    }

    const handleFocus = useCallback(
      e => {
        setIsFocused(true);

        if (onFocus) {
          onFocus(valueRef.current, name);
        }
      },
      [onFocus, name]
    );

    const handleBlur = useCallback(() => {
      setIsFocused(false);

      if (onBlur) {
        onBlur(valueRef.current, name);
      }
    }, [onBlur, name]);

    const handleChange = useCallback(
      value => {
        valueRef.current = value;

        if (onChange) {
          onChange(value, name);
        }
      },
      [onChange, name]
    );

    const handleKeyDown = useCallback(
      e => {
        if (onKeyDown) {
          onKeyDown(valueRef.current, name, e);
        }

        if (isNumber && onChange && !disableArrowKeyIncrement) {
          const value = e.target.value.trim().replace(/[$,]/g, '');
          let num = parseInt(value);

          if (isNaN(num)) {
            return;
          }

          const isDown = ['Down', 'ArrowDown'].includes(e.key);
          const isUp = ['Up', 'ArrowUp'].includes(e.key);

          if (isDown || isUp) {
            e.preventDefault();

            const diff = e.shiftKey ? 10 : 1;
            num = Math.max(isDown ? num - diff : num + diff, 0);

            onChange(String(num), name);
          }
        }
      },
      [onKeyDown, onChange, name, isNumber, disableArrowKeyIncrement]
    );

    const [props, icon] = useMemo(() => {
      const paddingProperty =
        iconPosition === 'right' ? 'paddingRight' : 'paddingLeft';
      const props = {
        id,
        value,
        type,
        validate,
        className: classNames(styles.input, className),
        style: { ...style, [paddingProperty]: iconPadding },
        onFocus: handleFocus,
        onBlur: handleBlur
      };

      if (isFormatted) {
        props.onValueChange = ({ value }) => handleChange(value);
        props.onKeyDown = handleKeyDown;
      } else {
        props.onChange = e => handleChange(e.target.value);
      }

      if (hasError && showErrorMessage) {
        props['aria-invalid'] = true;
        props['aria-errormessage'] = errorId;
      }

      let icon = _icon;

      if (isCurrency) {
        icon = icon ?? <span className={styles.currency} />;
        props.thousandSeparator = ',';
        props.allowNegative = allowNegative ?? false;
        props.decimalSeparator = '.';

        const padding = props.iconPadding ?? (size === 'small' ? 20 : 35);
        props.style = { ...props.style, [paddingProperty]: padding };
      }

      return [props, icon];
    }, [
      id,
      value,
      isFormatted,
      isCurrency,
      allowNegative,
      size,
      _icon,
      iconPosition,
      className,
      iconPadding,
      style,
      type,
      hasError,
      showErrorMessage,
      errorId,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyDown,
      validate
    ]);

    return (
      <Field
        inputId={id}
        label={label}
        className={fieldClassName}
        style={fieldStyle}
        theme={theme}
        size={size}
        inlineLabel={inlineLabel}
        maxWidth={maxWidth}
        errorId={errorId}
        errorMessage={errorMessage}
        showErrorMessage={showErrorMessage}
      >
        <div
          className={classNames('calculator-text-input', styles.wrapper, {
            // [styles.dark]: theme === 'dark',
            [styles.small]: size === 'small',
            [styles.leftIcon]: icon && iconPosition !== 'right',
            [styles.rightIcon]: icon && iconPosition === 'right',
            [styles.focus]: isFocused,
            [styles.hasError]: hasError
          })}
          style={{ width }}
        >
          {icon && (
            <div
              className={classNames(
                'calculator-text-input-icon',
                styles.icon,
                iconClassName
              )}
              style={{ ...iconStyle, left: iconLeft }}
            >
              {icon}
            </div>
          )}
          <>
            {isFormatted ? (
              <>
                {isPattern ? (
                  <PatternFormat
                    ref={ref}
                    format={format}
                    {...{ ...props, ...restProps }}
                  />
                ) : (
                  <NumericFormat
                    ref={ref}
                    decimalScale={0}
                    {...{ ...props, ...restProps }}
                  />
                )}
              </>
            ) : (
              <input ref={ref} {...{ ...props, ...restProps }} />
            )}
          </>
        </div>
      </Field>
    );
  })
);
